import * as React from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { RecipientsField } from './recipients-badge-input';
import type { ComposerFormValues } from '../schema';
import type { ComposerMode } from '../types';
import { Button } from '~/components/ui/button';
import { cn } from '~/lib/utils';

export function EmailFields({
  mode,
  lockTo,
}: {
  mode: ComposerMode;
  lockTo?: boolean;
}) {
  const { t } = useTranslation('mailbox');
  const { control, register, setValue } = useFormContext<ComposerFormValues>();
  const cc = useWatch({ control, name: 'cc' });
  // Cc visibility is the field's own concern, so it stays here rather than
  // being drilled down from the composer. Every default set of Cc recipients is
  // empty on first render, hence the plain `false`.
  const [isCcOpen, setIsCcOpen] = React.useState(false);
  const showCc = isCcOpen || cc.length > 0;
  // Reply recipients and subject are derived server-side; render all of them
  // read-only so the preview exactly matches what the server will send.
  const readOnly = mode === 'reply';
  const subjectReadOnly = mode !== 'new';
  const toReadOnly = readOnly || lockTo === true;

  function toggleCc() {
    if (showCc) {
      setValue('cc', [], {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      });
    }

    setIsCcOpen(!showCc);
  }

  return (
    <>
      <div className="flex min-h-8 items-center justify-between gap-2 rounded-md px-3">
        <span className="w-12 shrink-0 text-xs font-normal text-muted-foreground">
          {t('To')}
        </span>
        <div className="flex min-w-0 flex-1 flex-row items-center gap-2">
          <RecipientsField
            name="to"
            readOnly={toReadOnly}
            placeholder="email@example.com"
          />
        </div>
        {mode !== 'reply' && (
          <Button type="button" variant="muted" size="xs" onClick={toggleCc}>
            {t('Cc')}
          </Button>
        )}
      </div>

      {showCc && (
        <div className="flex min-h-8 items-center gap-2 rounded-md px-3">
          <span className="w-12 shrink-0 text-xs font-normal text-muted-foreground">
            {t('Cc')}
          </span>
          <div className="min-w-0 flex-1">
            <RecipientsField
              name="cc"
              readOnly={readOnly}
              placeholder="email@example.com"
            />
          </div>
        </div>
      )}

      <div className="flex min-h-8 items-center gap-2 rounded-md px-3">
        <span className="w-12 shrink-0 text-xs font-normal text-muted-foreground">
          {t('Subject')}
        </span>
        <input
          type="text"
          {...register('subject')}
          readOnly={subjectReadOnly}
          className={cn(
            'w-full border-0 bg-transparent text-xs font-normal text-foreground outline-none placeholder:text-muted-foreground',
            subjectReadOnly && 'text-muted-foreground',
          )}
        />
      </div>
    </>
  );
}
